import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Alert,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as MatchedIcon,
  Error as UnmatchedIcon,
  Link as LinkIcon,
  Visibility as ViewIcon,
  Compare as CompareIcon,
  AccountBalance as BankIcon,
  Receipt as TransactionIcon
} from '@mui/icons-material';

interface BankReconciliationProps {
  bankAccounts: any[];
  onRefresh: () => void;
  loading: boolean;
}

const BankReconciliation: React.FC<BankReconciliationProps> = ({
  bankAccounts,
  onRefresh,
  loading
}) => {
  const [selectedAccount, setSelectedAccount] = useState('');
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [selectedBankTransaction, setSelectedBankTransaction] = useState<any>(null);
  const [reconciling, setReconciling] = useState(false);

  // Mock reconciliation data
  const reconciliationData = {
    bank_balance: 15420.50,
    book_balance: 15380.25,
    difference: 40.25,
    matched_transactions: 45,
    unmatched_bank_transactions: 3,
    unmatched_book_transactions: 2,
    last_reconciliation: '2024-09-10'
  };

  const unmatchedBankTransactions = [
    {
      id: 'bank1',
      date: '2024-09-14',
      description: 'DIRECT DEBIT - UTILITIES LTD',
      amount: -125.50,
      amount_formatted: '-$125.50',
      reference: 'DD-20240914-001',
      suggested_matches: [
        { id: 'book1', description: 'Electricity Bill - September', amount: -125.50 }
      ]
    },
    {
      id: 'bank2',
      date: '2024-09-13',
      description: 'BANK CHARGES',
      amount: -15.00,
      amount_formatted: '-$15.00',
      reference: 'CHG-20240913-001',
      suggested_matches: []
    },
    {
      id: 'bank3',
      date: '2024-09-12',
      description: 'INTEREST RECEIVED',
      amount: 2.75,
      amount_formatted: '$2.75',
      reference: 'INT-20240912-001',
      suggested_matches: []
    }
  ];

  const unmatchedBookTransactions = [
    {
      id: 'book1',
      date: '2024-09-14',
      description: 'Electricity Bill - September',
      amount: -125.50,
      amount_formatted: '-$125.50',
      reference: 'EXP-2024-0156'
    },
    {
      id: 'book2',
      date: '2024-09-11',
      description: 'Office Rent - September',
      amount: -850.00,
      amount_formatted: '-$850.00',
      reference: 'EXP-2024-0155'
    }
  ];

  const handleAutoReconcile = async () => {
    setReconciling(true);
    // Simulate auto-reconciliation
    setTimeout(() => {
      setReconciling(false);
      onRefresh();
    }, 3000);
  };

  const handleManualMatch = (bankTransaction: any) => {
    setSelectedBankTransaction(bankTransaction);
    setShowMatchDialog(true);
  };

  const getReconciliationStatus = () => {
    if (Math.abs(reconciliationData.difference) < 0.01) {
      return { status: 'BALANCED', color: 'success', message: 'Account is balanced' };
    } else if (Math.abs(reconciliationData.difference) < 100) {
      return { status: 'MINOR_DIFFERENCE', color: 'warning', message: 'Minor difference detected' };
    } else {
      return { status: 'MAJOR_DIFFERENCE', color: 'error', message: 'Significant difference requires attention' };
    }
  };

  const reconciliationStatus = getReconciliationStatus();

  return (
    <Box>
      {/* Reconciliation Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Bank Reconciliation Summary
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Bank Account</InputLabel>
                <Select
                  value={selectedAccount}
                  label="Bank Account"
                  onChange={(e) => setSelectedAccount(e.target.value)}
                >
                  <MenuItem value="acc1">Chase - Business Checking</MenuItem>
                  <MenuItem value="acc2">Bank of America - Savings</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box>
                <Typography variant="body2" color="textSecondary">Bank Balance</Typography>
                <Typography variant="h6">
                  ${reconciliationData.bank_balance.toLocaleString('en-US')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box>
                <Typography variant="body2" color="textSecondary">Book Balance</Typography>
                <Typography variant="h6">
                  ${reconciliationData.book_balance.toLocaleString('en-US')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box>
                <Typography variant="body2" color="textSecondary">Difference</Typography>
                <Typography variant="h6" color={reconciliationStatus.color + '.main'}>
                  ${Math.abs(reconciliationData.difference).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip 
                  label={reconciliationStatus.status.replace('_', ' ')}
                  color={reconciliationStatus.color as any}
                  size="small"
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                startIcon={<CompareIcon />}
                onClick={handleAutoReconcile}
                disabled={reconciling}
                fullWidth
              >
                {reconciling ? 'Reconciling...' : 'Auto Reconcile'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reconciliation Status Alert */}
      <Alert severity={reconciliationStatus.color as any} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{reconciliationStatus.message}</strong>
          {reconciliationData.last_reconciliation && (
            <> • Last reconciliation: {new Date(reconciliationData.last_reconciliation).toLocaleDateString('en-US')}</>
          )}
        </Typography>
      </Alert>

      {/* Progress Indicator */}
      {reconciling && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" gutterBottom>
              Auto-reconciling transactions...
            </Typography>
            <LinearProgress />
          </CardContent>
        </Card>
      )}

      {/* Matching Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <MatchedIcon color="success" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Matched Transactions</Typography>
                  <Typography variant="h6">{reconciliationData.matched_transactions}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <BankIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Unmatched Bank Items</Typography>
                  <Typography variant="h6">{reconciliationData.unmatched_bank_transactions}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TransactionIcon color="error" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Unmatched Book Items</Typography>
                  <Typography variant="h6">{reconciliationData.unmatched_book_transactions}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Unmatched Bank Transactions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Unmatched Bank Transactions
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Suggested Matches</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unmatchedBankTransactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>
                      {new Date(transaction.date).toLocaleDateString('en-US')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                      >
                        {transaction.amount_formatted}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {transaction.reference}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.suggested_matches.length > 0 ? (
                        <Chip 
                          label={`${transaction.suggested_matches.length} match${transaction.suggested_matches.length !== 1 ? 'es' : ''}`}
                          color="info" 
                          size="small" 
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No matches
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton 
                        size="small"
                        onClick={() => handleManualMatch(transaction)}
                      >
                        <LinkIcon />
                      </IconButton>
                      <IconButton size="small">
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Unmatched Book Transactions */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Unmatched Book Transactions
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unmatchedBookTransactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>
                      {new Date(transaction.date).toLocaleDateString('en-US')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {transaction.description}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                      >
                        {transaction.amount_formatted}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {transaction.reference}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small">
                        <LinkIcon />
                      </IconButton>
                      <IconButton size="small">
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Manual Match Dialog */}
      <Dialog open={showMatchDialog} onClose={() => setShowMatchDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Manual Transaction Matching
        </DialogTitle>
        <DialogContent>
          {selectedBankTransaction && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Bank Transaction
              </Typography>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body2">
                    <strong>{selectedBankTransaction.description}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Amount: {selectedBankTransaction.amount_formatted}
                  </Typography>
                  <Typography variant="body2">
                    Date: {new Date(selectedBankTransaction.date).toLocaleDateString('en-US')}
                  </Typography>
                </CardContent>
              </Card>
              
              <Typography variant="h6" gutterBottom>
                Suggested Matches
              </Typography>
              {selectedBankTransaction.suggested_matches.map((match: any, index: number) => (
                <Card key={index} sx={{ mb: 1, cursor: 'pointer' }}>
                  <CardContent>
                    <Typography variant="body2">
                      {match.description}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Amount: ${Math.abs(match.amount).toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMatchDialog(false)}>Cancel</Button>
          <Button variant="contained">Match Selected</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankReconciliation;
